import DiscussionHistory from "./discussion-history/DiscussionHistory";
import DiscussionSearch from "./discussion-search/DiscussionSearch";
import { css, styled } from "styled-components";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  IMessagePayload,
  QUERIES,
  chatType,
  getDiscussionMessages,
  getDiscussionsHistory,
  dataSourcesInfo,
  IPaginationFilters,
  IDataSourceFilter,
  IDataSource,
  IPageableResponse,
  IConversation,
  IConversationMessage,
} from "@dataone/lib";
import { useMutation } from "@tanstack/react-query";

interface ChatProps {
  stepRef6: MutableRefObject<null>;
  stepRef7: MutableRefObject<null>;
}

const Chat = ({ stepRef6, stepRef7 }: ChatProps) => {
  const [newDiscussionStarted, setNewDiscussionStarted] = useState(false);
  const [selectedConversation, setSelectedConversation] =
    useState<IConversation>();
  const [conversation, setConversation] = useState<
    IPageableResponse<IConversationMessage>
  >({
    content: [],
    last: true,
    totalPages: 1,
    totalElements: 0,
    pageSize: 0,
    pageNo: 0,
  });
  const [messagePayLoad, setMessagePayLoad] = useState<IMessagePayload>({
    conversationId: "",
    input: "",
    chat: chatType.data,
    website: "",
    databaseId: "",
    workspaceUrl: "",
    email: "",
    token: "",
  });
  const [selectedOption, setSelectedOption] = useState(1);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { mutate: getDiscussion, isLoading: isLoadingConversation } =
    useMutation(getDiscussionMessages);
  const [conversationsPageNo, setConversationsPageNo] = useState(0);
  const [beforeDate, setBeforeDate] = useState(new Date());
  const [selectedDatasources, setSelectedDatasources] = useState<
    dataSourcesInfo[]
  >([]);
  const {
    data: discussionHistories,
    isLoading,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: [QUERIES.CHAT_CONVERSATIONS],
    queryFn: () => getDiscussionsHistory(conversationsPageNo),
  });
  const [conversationHistoryList, setConversationHistoryList] = useState<
    IPageableResponse<IConversation> | undefined
  >(discussionHistories);
  const [datasetsFilters, setDatasetsFilters] = useState<
    IPaginationFilters<IDataSourceFilter, IDataSource>
  >({
    body: { displayName: "" },
    pageNo: 0,
    beforeDate: new Date(),
    sortBy: "createdAt",
    sort: "DESC",
  });

  const handleNewDiscussionClick = () => {
    setNewDiscussionStarted(true);
    setSelectedConversation(undefined);
    setMessagePayLoad({
      conversationId: "",
      input: "",
      chat: chatType.data,
      website: "",
      databaseId: "",
      workspaceUrl: "",
      email: "",
      token: "",
    });
    setSelectedOption(1);
    setSelectedDatasources([]);
  };

  const fixState = () => {
    setNewDiscussionStarted(false);
  };

  const handleGetFromHistory = (
    conversation: IConversation,
    date?: Date,
    page?: number
  ) => {
    setSelectedConversation(conversation);
    if (!conversation.dataSources) setSelectedDatasources([]);
    setMessagePayLoad({
      conversationId: conversation.id,
      input: "",
      chat: conversation.chat || chatType.model,
      website: conversation.website || "",
    });
    getDiscussion(
      {
        conversationID: conversation.id || "",
        pageNo: page || 0,
        beforeDate: date || beforeDate,
      },
      {
        onSuccess: (data) => {
          if (data.pageNo === 0) {
            setConversation((prevConversation) => ({
              ...data,
              content: data.content,
            }));
            scrollBehavior.current = "auto";
          } else if (data?.content && typeof data?.pageNo === "number") {
            const { content, ...newMessages } = data;
            setConversation((prevConversation) => ({
              ...newMessages,
              content: [...prevConversation.content, ...content],
            }));
            setLoadingMore(false);
          }
        },
      }
    );
    setSelectedOption(
      conversation.chat === "DATA"
        ? 1
        : conversation.chat === "MODEL"
        ? 2
        : conversation.chat === "website"
        ? 3
        : conversation.chat === "jira"
        ? 5
        : 4
    );
  };

  const onDeleteSuccess = () => {
    refetchHistory();
    // setNewDiscussionStarted(true);
  };

  const [isLoadingMore, setLoadingMore] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const messagesRef = useRef<HTMLDivElement>(null);
  const bottomMessagesRef = useRef<HTMLElement>(null);
  const scrollBehavior = useRef<ScrollBehavior>("instant" as ScrollBehavior);
  const reachTheEndRef = useRef<HTMLElement>(null);

  // conversation history variables
  const [isLoadingMoreConversations, setLoadingMoreConversation] =
    useState(false);
  const [conversationsScrollPosition, setConversationScrollPosition] =
    useState(0);
  const conversationsRef = useRef<HTMLDivElement>(null);
  const topConversationsRef = useRef<HTMLElement>(null);
  const conversationsScrollBehavior = useRef<ScrollBehavior>(
    "instant" as ScrollBehavior
  );
  const reachTheEndRefOfConversations = useRef<HTMLElement>(null);

  useEffect(() => {
    if (conversation?.content && scrollBehavior.current) {
      bottomMessagesRef.current?.scrollIntoView({
        behavior: "instant" as ScrollBehavior,
        block: "end",
      });
      scrollBehavior.current = "instant" as ScrollBehavior;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          conversation?.content &&
          conversation?.content?.length < conversation?.totalElements &&
          entries[0].isIntersecting &&
          conversation?.content.at(-1)?.response &&
          conversation?.content.at(-1)?.response !== "..."
        ) {
          if (reachTheEndRef.current)
            observer.unobserve(reachTheEndRef.current);
          setLoadingMore(true);
          if (messagesRef.current)
            setScrollPosition(
              messagesRef.current.scrollHeight -
                messagesRef.current.scrollTop +
                15
            );

          setConversation((prevConversation) => ({
            ...prevConversation,
            pageNo: prevConversation.pageNo + 1,
          }));
        }
      },
      { threshold: 1 }
    );
    if (reachTheEndRef.current) observer.observe(reachTheEndRef.current);
    return () => {
      if (reachTheEndRef.current) {
        observer.unobserve(reachTheEndRef.current);
      }
    };
  }, [conversation?.content]);

  useEffect(() => {
    if (discussionHistories?.pageNo === 0) {
      setConversationHistoryList(discussionHistories);
    } else {
      setConversationHistoryList((prevConversations) => {
        if (!prevConversations) {
          return prevConversations;
        }
        return {
          ...prevConversations,
          pageNo: discussionHistories?.pageNo ?? 0,
          pageSize: discussionHistories?.pageSize ?? 0,
          totalElements: discussionHistories?.totalElements ?? 0, // Provide a default value
          totalPages: discussionHistories?.totalPages ?? 0, // Provide a default value
          last: discussionHistories?.last ?? false, // Provide a default value
          content: [
            ...prevConversations.content,
            ...(discussionHistories?.content || []),
          ],
        };
      });
    }
  }, [discussionHistories]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          conversationHistoryList?.content &&
          conversationHistoryList?.content?.length <
            conversationHistoryList?.totalElements &&
          entries[0].isIntersecting &&
          conversationHistoryList?.content.at(-1)?.label &&
          conversationHistoryList?.content.at(-1)?.label !== "..."
        ) {
          if (reachTheEndRefOfConversations.current)
            observer.unobserve(reachTheEndRefOfConversations.current);

          setLoadingMoreConversation(true);
          setConversationsPageNo((prevPageNo) => prevPageNo + 1);
          handleRefetch(conversationsPageNo + 1);
        }
      },
      { threshold: 1 }
    );
    if (reachTheEndRefOfConversations.current)
      observer.observe(reachTheEndRefOfConversations.current);
    return () => {
      if (reachTheEndRefOfConversations.current) {
        observer.unobserve(reachTheEndRefOfConversations.current);
      }
    };
  }, [conversationHistoryList?.content]);

  useEffect(() => {
    if (!isLoadingMore && scrollPosition) {
      if (messagesRef.current)
        messagesRef.current.scrollTop =
          messagesRef.current.scrollHeight - scrollPosition;
      setScrollPosition(0);
    }
  }, [isLoadingMore]);

  useEffect(() => {
    if (!isLoadingMoreConversations && conversationsScrollPosition) {
      if (conversationsRef.current)
        conversationsRef.current.scrollTop =
          conversationsRef.current.scrollHeight - conversationsScrollPosition;
      setConversationScrollPosition(0);
    }
  }, [isLoadingMoreConversations]);
  useEffect(() => {
    if (selectedConversation?.id) {
      handleGetFromHistory(
        selectedConversation as IConversation,
        undefined,
        conversation.pageNo
      );
    }
  }, [conversation.pageNo]);
  // useEffect(() => {
  //   handleRefetch(conversationsPageNo);
  // }, [conversationsPageNo]);
  const handleRefetch = (pageNo: number, itemAdded?: boolean) => {
    if (itemAdded) {
      setConversationsPageNo(0);
      scrollBehavior.current = "instant" as ScrollBehavior;
      setConversationScrollPosition(conversationsRef.current?.scrollTop ?? 0);
    }
    getDiscussionsHistory(pageNo).then((data) => {
      if (data.pageNo === 0) {
        setConversationHistoryList(data);
      } else if (data?.content && typeof data?.pageNo === "number") {
        const { content, ...newMessages } = data;
        setConversationHistoryList((prevConversation) => ({
          ...newMessages,
          content: [...(prevConversation?.content ?? []), ...content],
        }));
        setLoadingMoreConversation(false);
      } else if (
        data?.totalElements === conversationHistoryList?.totalElements
      ) {
        setLoadingMoreConversation(false);
      }
    });
  };
  return (
    <StyledContainer $collapsed={isCollapsed}>
      <div className="first-col">
        <DiscussionHistory
          onNewDiscussionClick={handleNewDiscussionClick}
          handleGetFromHistory={handleGetFromHistory}
          // discussionHistories={discussionHistories}
          discussionHistories={conversationHistoryList}
          isLoading={isLoading}
          onDeleteSuccess={onDeleteSuccess}
          // refetchHistory={refetchHistory}
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          selectedConversation={selectedConversation}
          setNewDiscussionStarted={setNewDiscussionStarted}
          reachTheEndRefOfConversations={reachTheEndRefOfConversations}
          conversationsRef={conversationsRef}
          topConversationsRef={topConversationsRef}
          isLoadingMoreConversations={isLoadingMoreConversations}
          handleRefetch={handleRefetch}
          setBeforeDate={setBeforeDate}
        />
      </div>
      <div className="second-col">
        <DiscussionSearch
          stepRef6={stepRef6}
          stepRef7={stepRef7}
          fixState={fixState}
          isNewDiscussionStarted={newDiscussionStarted}
          conversation={conversation}
          setConversation={setConversation}
          refetchHistory={refetchHistory}
          discussionHistories={conversationHistoryList}
          selectedConversation={selectedConversation}
          messagePayLoad={messagePayLoad}
          setMessagePayLoad={setMessagePayLoad}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          setSelectedDatasources={setSelectedDatasources}
          selectedDatasources={selectedDatasources}
          // setDatasetsFilters={setDatasetsFilters}
          bottomMessagesRef={bottomMessagesRef}
          messagesRef={messagesRef}
          reachTheEndRef={reachTheEndRef}
          isLoadingMore={isLoadingMore}
          handleRefetch={handleRefetch}
          isLoadingConversation={isLoadingConversation}
          historyCollapsed={isCollapsed}
        />
      </div>
    </StyledContainer>
  );
};

export default Chat;

const StyledContainer = styled.div<{ $collapsed: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  .first-col {
    position: relative;
    width: 25%;
    // min-width: 300px;
    // height: 120vh;
    transition: min-width 0.2s ease-in-out;
    ${(props) =>
      props.$collapsed &&
      css`
        width: 0px;
        min-width: 0px;
      `}
  }
  .second-col {
    width: 75%;
    // height: 100vh;
    transition: width 0.2s ease-in-out;
    ${(props) =>
      props.$collapsed &&
      css`
        width: 90%;
      `}
  }
`;
